import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useItemSubCategoryApi } from '../../hooks/useItemSubCategory';
import { CreateItemCategoryRequest } from '../../client-sdk';
import { useSnackbar } from '../Stateless/SnackBarProvider';

export interface ItemCategory {
  id: string;
  nameEnglish: string;
  nameArabic: string;
  status: 'blocked' | 'unblocked' | string;
  addedBy: string;
  categoryImage?: string;
  categoryIcon?: string;
  subCategories?: ItemCategory[];
}

export const useItemAddCategory = () => {
  const { addItemCategory } = useApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationFn: (itemCategoryData: CreateItemCategoryRequest) =>
      addItemCategory(itemCategoryData),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['itemCategories'] });
      showMessage(response?.message || 'Store created successfully', 'success');
    },
    onError: (error: any) => {
      console.error('Error adding category:', error);
      showMessage(
        error.response?.data?.message || 'Failed to create Store',
        'error'
      );
    },
  });
};

type Params = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
  storeId?: string;
};

export const useGetItemCategories = (queryParams: Params) => {

  const { getListItemCategories } = useApi();

  const { data, isLoading, isRefetching, refetch, error } = useQuery({
    queryKey: [
      'itemCategories',
      queryParams.page,
      queryParams.limit,
      queryParams.sortBy,
      queryParams.sortOrder,
      queryParams.search,
      queryParams.statusFilter,
      queryParams.addedByFilter,
      queryParams.storeId
    ],
    queryFn: () => {
      return getListItemCategories(queryParams);
    },
    enabled: true, // Ensure the query is enabled
  });


  return {
    data,
    isLoading,
    isRefetching,
    refetch,
    error,
  };
};

export const useEditItemCategory = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editItemCategory } = useApi();

  return useMutation({
    mutationKey: ['editItemSubCategory'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editItemCategory(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Item SubCategory Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['itemCategories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update the Item SubCategory',
        'error'
      );
    },
  });
};

export const useDeleteItemSubCategory = () => {
  const queryClient = useQueryClient();
  const { deleteItemSubCategory } = useItemSubCategoryApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['deleteItemSubCategory'],
    mutationFn: (ids: string[]) => {
      return deleteItemSubCategory(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Item SubCategory deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['itemSubCategories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Item SubCategory',
        'error'
      );
    },
  });
};

export const useBlockUnblockItemCategory = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { blockUnblockItemCategoryById } = useApi();

  return useMutation({
    mutationKey: ['blockUnblockItemCategory'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      blockUnblockItemCategoryById({ id, status: data.status }),

    onSuccess: (_response, _variables) => {
      const status = _variables.data.status;
      const action = status === 'blocked' ? 'blocked' : 'unblocked';
      showMessage(`ItemCategory ${action} successfully`, 'success');

      queryClient.invalidateQueries({
        queryKey: ['itemCategories'],
        exact: false,
      });
    },

    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update ItemCategory status',
        'error'
      );
    },
  });
};

export const useDeleteItemCategory = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { deleteItemCategoryById } = useApi();

  return useMutation({
    mutationKey: ['deleteItemCategory'],
    mutationFn: (ids: string[]) => {
      return deleteItemCategoryById(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Item Category deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['itemCategories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Item Category',
        'error'
      );
    },
  });
};
